import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Client } from "@/types/client";
import { Download, Eye, Trash2, Upload, X, FileText, Image, File } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface ClientFilesDialogProps {
  client: Client | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ClientFilesDialog = ({ client, open, onOpenChange }: ClientFilesDialogProps) => {
  const [uploading, setUploading] = useState(false);

  const getFileIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension || '')) {
      return <Image className="w-6 h-6 text-blue-500" />;
    } else if (['pdf'].includes(extension || '')) {
      return <FileText className="w-6 h-6 text-red-500" />;
    } else {
      return <File className="w-6 h-6 text-gray-500" />;
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !client) return;

    setUploading(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${client.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('client-attachments')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        return fileName;
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      
      // تحديث قاعدة البيانات بالملفات الجديدة
      const currentAttachments = client.attachments || [];
      const newAttachments = [...currentAttachments, ...uploadedFiles];

      const { error: updateError } = await supabase
        .from('clients')
        .update({ attachments: newAttachments })
        .eq('id', client.id);

      if (updateError) throw updateError;

      toast.success(`تم رفع ${uploadedFiles.length} ملف بنجاح`);
      
      // إعادة تحديث البيانات
      window.location.reload();
      
    } catch (error: any) {
      console.error('Error uploading files:', error);
      toast.error('حدث خطأ أثناء رفع الملفات');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadFile = async (fileName: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('client-attachments')
        .download(fileName);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName.split('/').pop() || 'file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('تم تحميل الملف بنجاح');
    } catch (error: any) {
      console.error('Error downloading file:', error);
      toast.error('حدث خطأ أثناء تحميل الملف');
    }
  };

  const handleViewFile = async (fileName: string) => {
    try {
      const { data } = supabase.storage
        .from('client-attachments')
        .getPublicUrl(fileName);

      if (data?.publicUrl) {
        window.open(data.publicUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Error viewing file:', error);
      toast.error('حدث خطأ أثناء عرض الملف');
    }
  };

  const handleDeleteFile = async (fileName: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا الملف؟')) return;

    try {
      const { error: deleteError } = await supabase.storage
        .from('client-attachments')
        .remove([fileName]);

      if (deleteError) throw deleteError;

      // تحديث قاعدة البيانات
      const currentAttachments = client?.attachments || [];
      const newAttachments = currentAttachments.filter(file => file !== fileName);

      const { error: updateError } = await supabase
        .from('clients')
        .update({ attachments: newAttachments })
        .eq('id', client?.id);

      if (updateError) throw updateError;

      toast.success('تم حذف الملف بنجاح');
      
      // إعادة تحديث البيانات
      window.location.reload();
      
    } catch (error: any) {
      console.error('Error deleting file:', error);
      toast.error('حدث خطأ أثناء حذف الملف');
    }
  };

  const attachments = client?.attachments || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="text-xl font-bold">
            ملفات العميل: {client?.name_ar}
          </DialogTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </DialogHeader>

        <div className="space-y-4">
          {/* قسم رفع الملفات */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">رفع ملفات جديدة</h3>
                <div className="relative">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={uploading}
                  />
                  <Button disabled={uploading} className="flex items-center gap-2">
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        جاري الرفع...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        اختر ملفات
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* قائمة الملفات */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">الملفات المرفوعة</h3>
              <Badge variant="secondary">
                {attachments.length} ملف
              </Badge>
            </div>

            {attachments.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">لا توجد ملفات مرفوعة لهذا العميل</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-2">
                {attachments.map((fileName, index) => {
                  const displayName = fileName.split('/').pop() || fileName;
                  
                  return (
                    <Card key={index} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {getFileIcon(displayName)}
                            <div>
                              <p className="font-medium">{displayName}</p>
                              <p className="text-sm text-muted-foreground">
                                ملف رقم {index + 1}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewFile(fileName)}
                              className="h-8 px-2"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadFile(fileName)}
                              className="h-8 px-2"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteFile(fileName)}
                              className="h-8 px-2 text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};